import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { hitGetServicesCommonApi } from "../../../Redux/Common/GetServicesCommonSlice";

const ProfessionalServiceSelection = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const [servicesData, setServicesData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await dispatch(hitGetServicesCommonApi("all-services"));

      if (hitGetServicesCommonApi.rejected.match(response)) {
        setServicesData([]);
        return;
      }

      if (response.payload?.status === true) {
        const data = response.payload.data;
        const list = data?.subServices || (Array.isArray(data) ? data : []);
        setServicesData(list);
      } else {
        setServicesData([]);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      setServicesData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const availableServices = useMemo(() => {
    const query = search.trim().toLowerCase();
    return servicesData
      .filter((service) => !value.some((selected) => selected._id === service._id))
      .filter(
        (service) =>
          !query || service.name?.toLowerCase().includes(query),
      );
  }, [servicesData, value, search]);

  const showDropdown = isOpen && !disabled;

  const handleServiceClick = (service) => {
    if (disabled) return;
    if (value.some((selected) => selected._id === service._id)) return;
    onChange?.([...value, service]);
    setSearch("");
  };

  const handleRemoveService = (serviceId) => {
    if (disabled) return;
    onChange?.(value.filter((service) => service._id !== serviceId));
  };

  return (
    <div className="pro-service-selection" ref={containerRef}>
      <label>
        Services <span className="pro-service-selection__required">*</span>
        {value.length > 0 && (
          <span className="pro-service-selection__count">
            {value.length} selected
          </span>
        )}
      </label>

      {value.length > 0 && (
        <div className="pro-service-selection__selected">
          {value.map((service) => (
            <span className="pro-service-selection__chip" key={service._id}>
              {service.name}
              {!disabled && (
                <button
                  type="button"
                  className="pro-service-selection__chip-remove"
                  onClick={() => handleRemoveService(service._id)}
                  aria-label={`Remove ${service.name}`}
                >
                  <CloseIcon />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="pro-service-selection__field">
        <input
          type="text"
          placeholder="Search services"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
          autoComplete="off"
        />

        {showDropdown && (
          <div className="pro-service-selection__dropdown">
            {loading ? (
              <p className="pro-service-selection__empty">Loading...</p>
            ) : availableServices.length > 0 ? (
              availableServices.map((service) => (
                <button
                  type="button"
                  key={service._id}
                  className="pro-service-selection__option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleServiceClick(service)}
                >
                  {service.name}
                </button>
              ))
            ) : (
              <p className="pro-service-selection__empty">
                {search.trim() ? "No matching services" : "All services selected"}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalServiceSelection;
